import type { Item } from "@iltocat/shared";
import { fetchPhoto } from "./api.js";
import type { ItemPhotos } from "./modelChoice.js";
import { fitWithin } from "./photos.js";

/**
 * The items' photos, made small enough to send to Claude with the prompt.
 *
 * **1568px on the long edge**, which is Anthropic's own guidance: a larger
 * image is scaled down on their side anyway, so sending the stored 2048px copy
 * would only be a slower upload for the same answer. Re-encoded as JPEG at
 * 0.8, a photo is a few hundred kilobytes, which keeps ten items' worth of
 * request well inside the API's size limit on a phone connection.
 *
 * **The first few per item**, cover first: the cover and the next couple are
 * usually the whole object and its label, and an item can hold a dozen.
 *
 * Photos come from utucat's own photo route (`fetchPhoto`), with the session,
 * like every other photo on screen; only their resized copies go to Anthropic.
 * One that will not load is left out and counted, not fatal: the text prompt
 * still stands on its own.
 */

export const MODEL_PHOTO_EDGE = 1568;
export const PHOTOS_PER_ITEM = 3;
const QUALITY = 0.8;

/** How many photos would go, for saying so before anything is sent. */
export function photosToSend(items: readonly Item[]): number {
  return items.reduce((total, item) => total + Math.min(item.photos.length, PHOTOS_PER_ITEM), 0);
}

export async function loadModelPhotos(
  items: readonly Item[],
  signal?: AbortSignal,
): Promise<{ photos: ItemPhotos[]; failed: number }> {
  let failed = 0;
  const photos: ItemPhotos[] = [];
  for (const item of items) {
    const images: ItemPhotos["images"] = [];
    for (const photo of item.photos.slice(0, PHOTOS_PER_ITEM)) {
      if (signal?.aborted) return { photos, failed };
      try {
        images.push({ mediaType: "image/jpeg", data: await shrink(await fetchPhoto(photo.id)) });
      } catch {
        failed++;
      }
    }
    photos.push({ itemId: item.id, images });
  }
  return { photos, failed };
}

/** Bytes of a stored JPEG in, base64 of a smaller one out. The stored copy is already upright. */
async function shrink(bytes: Uint8Array): Promise<string> {
  const bitmap = await createImageBitmap(new Blob([bytes as BlobPart], { type: "image/jpeg" }));
  try {
    const size = fitWithin(bitmap.width, bitmap.height, MODEL_PHOTO_EDGE);
    const canvas = document.createElement("canvas");
    canvas.width = size.width;
    canvas.height = size.height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("No canvas to resize on.");
    context.imageSmoothingQuality = "high";
    context.drawImage(bitmap, 0, 0, size.width, size.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", QUALITY));
    if (!blob) throw new Error("The canvas would not encode.");
    return base64(new Uint8Array(await blob.arrayBuffer()));
  } finally {
    bitmap.close();
  }
}

/** In slices: `String.fromCharCode(...bytes)` on a whole photo overflows the call stack. */
export function base64(bytes: Uint8Array): string {
  let binary = "";
  for (let start = 0; start < bytes.length; start += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(start, start + 0x8000));
  }
  return btoa(binary);
}
